import React, { useState, useEffect } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Crown, Sparkles, BarChart3, Calendar, Wand2, Shield, Zap,
  TrendingUp, Users, Heart, MessageCircle, Eye, Clock, 
  Check, Lock, ArrowRight, Download, Settings, Star
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Premium = ({ user }) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  const isPremium = user?.subscription_tier && user.subscription_tier !== 'basic';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, analyticsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/subscriptions/current`, { withCredentials: true }),
        axios.get(`${API_URL}/premium/analytics`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/subscriptions/history`, { withCredentials: true }).catch(() => ({ data: { transactions: [] } }))
      ]);
      
      setSubscription(subRes.data);
      setAnalytics(analyticsRes.data);
      setHistory(historyRes.data.transactions || []);
    } catch (error) {
      console.log('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      return;
    }
    
    try {
      await axios.post(`${API_URL}/subscriptions/cancel`, {}, { withCredentials: true });
      toast.success('Subscription cancelled');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Not premium - show upgrade prompt
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="premium-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-black text-gray-900 dark:text-white mb-4">
              Unlock Premium
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-xl mx-auto">
              Get analytics, AI content generation, ad-free experience, and much more.
            </p>
          </motion.div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Track your growth with detailed insights', color: 'from-blue-500 to-cyan-500' },
              { icon: Wand2, title: 'AI Content Generator', desc: 'Generate posts, captions, and ideas instantly', color: 'from-purple-500 to-pink-500' },
              { icon: Calendar, title: 'Post Scheduling', desc: 'Plan and schedule your content in advance', color: 'from-green-500 to-emerald-500' },
              { icon: Shield, title: 'Priority Verification', desc: 'Get verified faster with priority review', color: 'from-yellow-500 to-orange-500' },
              { icon: Zap, title: 'Ad-Free Experience', desc: 'Browse without any interruptions', color: 'from-red-500 to-pink-500' },
              { icon: Star, title: 'Premium Badge', desc: 'Stand out with a premium badge on your profile', color: 'from-primary to-accent' },
            ].map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="p-6 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:shadow-2xl transition-all">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center flex-shrink-0`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{feature.desc}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <Link to="/pricing">
              <Button className="rounded-full bg-gradient-to-r from-primary to-accent text-white font-bold px-8 py-6 text-lg shadow-xl hover:scale-105 transition-transform">
                View Plans & Pricing
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
              Starting at $9.99/month. Cancel anytime.
            </p>
          </motion.div>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  // Premium user dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="premium-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-black text-gray-900 dark:text-white">
                Premium Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {subscription?.name || 'Pro'} Plan
              </p>
            </div>
          </div>
          <Link to="/pricing">
            <Button variant="outline" className="rounded-full">
              Manage Plan
            </Button>
          </Link>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Views', value: analytics?.total_views || 0, icon: Eye, color: 'text-blue-500' },
            { label: 'Followers', value: analytics?.followers || user?.followers_count || 0, icon: Users, color: 'text-purple-500' },
            { label: 'Total Likes', value: analytics?.total_likes || 0, icon: Heart, color: 'text-red-500' },
            { label: 'Comments', value: analytics?.total_comments || 0, icon: MessageCircle, color: 'text-green-500' },
          ].map((stat, idx) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <stat.icon className={`w-8 h-8 ${stat.color}`} />
                  <div>
                    <p className="text-2xl font-heading font-bold text-gray-900 dark:text-white">{stat.value}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{stat.label}</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <Card className="p-6 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Premium Features</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { icon: BarChart3, label: 'Analytics', path: '/premium/analytics', color: 'from-blue-500 to-cyan-500' },
                  { icon: Calendar, label: 'Schedule', path: '/premium/schedule', color: 'from-green-500 to-emerald-500' },
                  { icon: Wand2, label: 'AI Generator', path: '/premium/ai', color: 'from-purple-500 to-pink-500' },
                  { icon: Shield, label: 'Verification', path: '/settings', color: 'from-yellow-500 to-orange-500' },
                  { icon: Download, label: 'Export Data', path: '/settings', color: 'from-gray-500 to-gray-700' },
                  { icon: Settings, label: 'Settings', path: '/settings', color: 'from-red-500 to-pink-500' },
                ].map((item) => (
                  <Link key={item.label} to={item.path}>
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-center">
                      <div className={`w-10 h-10 mx-auto rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-2`}>
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.label}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>

            {/* Growth Chart Placeholder */}
            <Card className="p-6 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Growth Overview
                </h2>
                <span className="text-sm text-gray-500">Last 30 days</span>
              </div>
              <div className="h-48 flex items-center justify-center bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="text-center">
                  <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">Analytics chart coming soon</p>
                  <p className="text-sm text-gray-400">Post more content to see your growth</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Current Plan */}
            <Card className="p-6 rounded-2xl border border-white/20 shadow-xl bg-gradient-to-br from-primary/10 to-accent/10 dark:from-primary/20 dark:to-accent/20 backdrop-blur-md">
              <div className="flex items-center gap-3 mb-4">
                <Crown className="w-8 h-8 text-primary" />
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">{subscription?.name || 'Pro'} Plan</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                {(subscription?.features || ['Analytics Dashboard', 'AI Content Generator', 'Ad-free experience']).slice(0, 4).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/pricing">
                <Button variant="outline" size="sm" className="w-full rounded-full">
                  Upgrade Plan
                </Button>
              </Link>
            </Card>

            {/* Payment History */}
            <Card className="p-6 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">Payment History</h3>
              {history.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No transactions yet
                </p>
              ) : (
                <div className="space-y-3">
                  {history.slice(0, 3).map((txn, idx) => (
                    <div key={txn.transaction_id || idx} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white capitalize">{txn.tier} Plan</p>
                        <p className="text-xs text-gray-500">{new Date(txn.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">${txn.amount}</p>
                        <p className={`text-xs ${txn.status === 'completed' ? 'text-green-500' : 'text-yellow-500'}`}>
                          {txn.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Cancel */}
            <Button
              onClick={cancelSubscription}
              variant="ghost"
              className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              Cancel Subscription
            </Button>
          </div>
        </div>
      </div>
      <FloatingDock user={user} />
    </div>
  );
};

export default Premium;
